import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { userApi } from 'src/api/userApi';
import { subscribeUserProfile } from 'src/api/userProfileLoader';
import type { User } from 'src/types/user';
import { isRequestAbortedError } from 'src/utils/axios/isRequestAbortedError';
import useAuth from 'src/hooks/useAuth';

type UserProfileContextValue = {
  userData: User | null;
  loading: boolean;
  error: Error | null;
  loadProfile: () => Promise<void>;
};

const UserProfileContext = createContext<UserProfileContextValue | null>(null);

export const UserProfileProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitialized } = useAuth();
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => subscribeUserProfile(setUserData), []);

  useEffect(() => {
    if (!isInitialized || !isAuthenticated) {
      setUserData(null);
      setLoading(false);
      setError(null);
    }
  }, [isAuthenticated, isInitialized]);

  const loadProfile = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await userApi.getByKeycloakId();
    } catch (err) {
      if (!isRequestAbortedError(err)) {
        setError(err as Error);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const value = useMemo(
    () => ({ userData, loading, error, loadProfile }),
    [userData, loading, error, loadProfile]
  );

  return (
    <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>
  );
};

export const useUserProfile = (): UserProfileContextValue => {
  const context = useContext(UserProfileContext);

  if (!context) {
    throw new Error('useUserProfile must be used within UserProfileProvider');
  }

  return context;
};
